import React from 'react';
import clsx from 'clsx';
import { makeStyles } from '@lifeomic/chroma-react/styles/makeStyles';
import { Modal, ModalProps } from '@lifeomic/chroma-react/components/Modal';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { X } from '@lifeomic/chromicons';
import { defineMessages, useIntl } from 'react-intl';
import backgroundImage from '../../static/images/wave-dot-matrix.svg';

const messages = defineMessages({
  close: {
    id: 'PresentationModal.close',
    defaultMessage: 'Close',
  },
});

const useStyles = makeStyles<{ viewMode?: string }>(theme => ({
  modal: {
    // TODO: remove print view from presentation logic
    backgroundColor: ({ viewMode }) =>
      viewMode === 'print' ? 'white' : '#105dac',
    backgroundImage: ({ viewMode }) =>
      viewMode === 'print'
        ? 'none'
        : `radial-gradient(
      farthest-side at bottom left,
      #112a70,
      transparent
    ),
    radial-gradient(
      farthest-corner at top right,
      #08a5d2,
      transparent 65%
    )`,
    position: 'relative',
    '& > div:first-child': {
      display: 'none',
    },
    overflow: 'hidden',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  modalContent: {
    backgroundColor: 'transparent',
    display: 'flex',
    gap: theme.spacing(2.5),
    padding: `0 ${theme.pxToRem(16)} 0 ${theme.pxToRem(16)}`,
    justifyContent: 'center',
    width: '100%',
  },
  actionButton: {
    backgroundColor: 'rgba(0, 0, 0, 0.65)',
    borderRadius: theme.pxToRem(99),
    width: 'fit-content',
  },
  closeButton: {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
  },
  backgroundImage: ({ viewMode }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    opacity: 0.075,
    pointerEvents: 'none',
    zIndex: 2,
    ...(viewMode === 'print' && {
      display: 'none',
    }),
  }),
}));

export const PresentationAction = React.forwardRef<
  HTMLButtonElement,
  {
    title: string;
    className?: string;
    icon: React.MemoExoticComponent<
      (props: React.SVGProps<SVGSVGElement>) => JSX.Element
    >;
  } & Omit<React.ComponentPropsWithoutRef<typeof IconButton>, 'aria-label'>
>(({ title, className, icon, ...rest }, ref) => {
  const classes = useStyles({});
  return (
    <IconButton
      // We forward any ref so this component will work as an anchor for a
      // popover.
      ref={ref}
      aria-label={title}
      title={title}
      className={clsx(classes.actionButton, className)}
      color="inverse"
      icon={icon}
      size={0}
      {...rest}
    />
  );
});

// Optional but helpful for React DevTools
PresentationAction.displayName = 'PresentationAction';

export type PresentationModalProps = ModalProps & {
  viewMode?: string;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  actions?: React.ReactNode;
};

/**
 * A full-screen branded modal that can be used for presenting content to members.
 */
export const PresentationModal = ({
  children,
  viewMode,
  onDismiss,
  actions,
  ...props
}: PresentationModalProps) => {
  const classes = useStyles({ viewMode });
  const { formatMessage } = useIntl();

  return (
    <Modal
      className={classes.modal}
      contentClassName={classes.modalContent}
      isFullScreen
      onDismiss={onDismiss}
      {...props}
    >
      <Box className={classes.backgroundImage} />
      {children}
      <Box direction="column" gap={1}>
        <PresentationAction
          title={formatMessage(messages.close)}
          className={classes.closeButton}
          icon={X}
          onClick={e => onDismiss?.(e)}
        />
        {actions}
      </Box>
    </Modal>
  );
};
